import AspectRatio from '@/components/basic/aspect-ratio';
import React, {useContext, useMemo} from 'react';
import {Button} from 'antd-mobile';
import {Trans, useTranslation} from 'react-i18next';
import {cn, goTo, toPriceStr} from '@/utils';
import {WeeklyCardType} from '../vip.variable';

import {useLogin} from '@/utils/state-hooks';
import {WeekCardContext} from './context';
import globalStore from '@/services/global.state';
interface PrizeItem {
  spin: number;
  scratchCard: number;
  money: number;
  immediateGet: number;
}

export interface CardItemProps {
  cardId?: number;
  type: WeeklyCardType;
  prizes: PrizeItem;
  price: number;
}

const RewardItem = ({
  type,
  value,
  className,
}: {
  type: 'money' | 'spin' | 'scratchCard';
  value: number;
  className?: string;
}) => {
  const pic = useMemo(() => {
    const map = {
      money: require('@/assets/icons/vip/v2/money.webp'),
      spin: require('@/assets/icons/vip/v2/spin.webp'),
      scratchCard: require('@/assets/icons/vip/v2/scratch.webp'),
    };
    return map[type];
  }, [type]);
  return (
    <div className={cn('flex flex-row items-center', className)}>
      <div
        className="size-3 bg-full bg-no-repeat"
        style={{backgroundImage: `url(${pic})`}}
      />
      <span
        className={cn(
          'text-[#FFF600] text-[.8125rem] font-bold leading-[normal]',
        )}>
        x{type === 'money' ? toPriceStr(value, {fixed: 0}) : value}
      </span>
    </div>
  );
};

const CardItem = ({type, prizes, cardId, price}: CardItemProps) => {
  const pic = useMemo(() => {
    const bgMap = {
      silver: require('@/assets/imgs/vip/v2/week/silver-card-bg.webp'),
      gold: require('@/assets/imgs/vip/v2/week/gold-card-bg.webp'),
      diamond: require('@/assets/imgs/vip/v2/week/diamond-card-bg.webp'),
    };
    const bonusMap = {
      silver: require('@/assets/imgs/vip/v2/week/silver-bonus.webp'),
      gold: require('@/assets/imgs/vip/v2/week/gold-bonus.webp'),
      diamond: require('@/assets/imgs/vip/v2/week/diamond-bonus.webp'),
    };
    return {
      bg: bgMap[type],
      bonus: bonusMap[type],
    };
  }, [type]);

  const {t} = useTranslation();
  const login = useLogin();
  const {level} = useContext(WeekCardContext);
  const showConfirm = () => {
    if (!login) {
      goTo('Login');
      return;
    }
    if (cardId == null) {
      return;
    }
    if (level <= 0) {
      globalStore.globalWaringTotal(t('vip.tip.noVip'));
      setTimeout(() => {
        goTo('Recharge');
      }, 1000);
      return;
    }
    goTo('Recharge', {from: 'weekCard', cardId});
  };

  const prePrice = useMemo(() => {
    const preMulMap = {
      silver: 1.5,
      gold: 1.75,
      diamond: 2,
    };
    return price * preMulMap[type];
  }, [price, type]);

  const totalMoney = useMemo(() => {
    const totalMulMap = {
      silver: 1.5,
      gold: 2,
      diamond: 3,
    };
    return prePrice * totalMulMap[type];
  }, [prePrice, type]);
  return (
    <AspectRatio ratio={351 / 213} onClick={showConfirm}>
      <div
        className="size-full bg-image bg-full overflow-visible bg-no-repeat flex flex-col justify-end px-[1.375rem] pb-4 rounded-lg"
        style={{
          backgroundImage: `url(${pic.bg})`,
        }}>
        <div
          className="absolute -top-4 -left-2 size-[5.5625rem] z-[2] bg-full bg-no-repeat"
          style={{
            backgroundImage: `url(${pic.bonus})`,
          }}
        />
        <div className="absolute z-[2] w-[5rem] top-[2.6875rem] right-[11.75rem] text-right text-white/55 text-[1.125rem] font-bold uppercase leading-[normal]">
          {t('vip.label.weekCard')}
        </div>
        {/* 奖励区域 */}
        <div className="flex flex-row gap-4 items-end">
          <ul
            className={cn(
              'flex-1 flex-col flex gap-1 text-[0.625rem] tracking-tighter',
              type === 'silver' && 'text-[#B4DEFF]',
              type === 'gold' && 'text-[#A2824F]',
              type === 'diamond' && 'text-[#A86E74]',
            )}>
            <li className="leading-[normal]">
              <span className="font-bold text-xs mr-1">•</span>
              <Trans
                i18nKey="vip.tip.weekCard.p1"
                values={{money: toPriceStr(price, {fixed: 0})}}
                components={[
                  <span className="text-[#FFF600] font-bold text-xs" />,
                ]}
              />
            </li>
            <li className="leading-[normal]">
              <span className="font-bold text-xs mr-1">•</span>
              <span>{t('vip.tip.weekCard.p2')}</span>
              <div className="flex flex-row justify-between">
                <RewardItem type="money" value={prizes.money} />
                <RewardItem type="spin" value={prizes.spin} />
                <RewardItem type="scratchCard" value={prizes.scratchCard} />
              </div>
            </li>
            <li className="leading-[normal]">
              <span className="font-bold text-xs mr-1">•</span>
              <Trans
                i18nKey="vip.tip.weekCard.p3"
                values={{money: toPriceStr(totalMoney, {fixed: 0})}}
                components={[
                  <span className="text-[#FFF600] font-bold text-xs" />,
                ]}
              />
            </li>
          </ul>
          <Button className="w-[8.75rem] mb-3 py-1 px-0 bg-c1 rounded-full [&_span]:flex [&_span]:flex-col [&_span]:items-center [&_span]:text-white [&_span]:font-bold">
            <span className="line-through text-[0.625rem]">
              {toPriceStr(prePrice, {fixed: 2})}
            </span>
            <span className="text-base">{toPriceStr(price, {fixed: 2})}</span>
          </Button>
        </div>
      </div>
    </AspectRatio>
  );
};

export default CardItem;
